/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import org.w3c.dom.url.URLSearchParams
import systems.frictionless.analytics.Analytics
import systems.frictionless.impulse.browser.routing.Route
import systems.frictionless.impulse.browser.viewmodel.RoutingViewModel
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.internal.Base64
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.EmailValidator
import systems.frictionless.impulse.rest.route.UserPath
import systems.frictionless.impulse.viewmodel.ChangePasswordViewModel
import systems.frictionless.impulse.viewmodel.DeleteAccountViewModel
import systems.frictionless.impulse.viewmodel.ForgotPasswordViewModel
import systems.frictionless.impulse.viewmodel.WizardViewModel

/*
 * There is currently a two-tier hierarchy to the UI:
 *  1. Route - This is the first tier allows for deep-linking into different parts of the application.
 *  2. Wizard - The typical "happy path" flow of the user through the application.
 *
 * In the future, this design could change a bit.
 */
@Composable
fun MainView(
    restClient: RestClient,
    routingViewModel: RoutingViewModel,
    wizardViewModel: WizardViewModel,
    @Suppress("UNUSED_PARAMETER") analytics: Analytics
) {
    val onForgotPassword: () -> Unit = {
        // Need to consider whether forgot password is a separate route or handled inside of the login view model
        routingViewModel.changeRoute(Route.ForgotPassword)
    }

    when (routingViewModel.state.collectAsState().value) {
        Route.Wizard -> {
            val searchParams = URLSearchParams(routingViewModel.initialQueryString)
            HandleWizard(wizardViewModel, searchParams.isShowLastResult(), onForgotPassword)
        }
        Route.ChangePassword -> {
            val searchParams = URLSearchParams(routingViewModel.initialQueryString)

            val email = searchParams.getEmail()
            val token = searchParams.get(UserPath.ChangePassword.TOKEN)

            if (null != email && null != token) {
                val vm = ChangePasswordViewModel(restClient, email, Authentication.Bearer(token))
                ChangePasswordSection(vm) {
                    wizardViewModel.deepLinkToPostSignIn(it)

                    routingViewModel.changeRoute(Route.Wizard)
                }
            } else {
                // Need to report error via analytics if URL parameters couldn't be parsed

                routingViewModel.changeRoute(Route.Wizard)
            }
        }
        Route.ForgotPassword -> {
            ForgotPassword(ForgotPasswordViewModel(restClient))
        }
        Route.Account -> {
            DeleteAccount(DeleteAccountViewModel(restClient), onForgotPassword) {
                routingViewModel.changeRoute(Route.Wizard)
            }
        }
    }
}

@Composable
private fun HandleWizard(
    wizardViewModel: WizardViewModel,
    isViewLastResult: Boolean,
    onForgotPassword: () -> Unit
) {
    when (val wizardStateSnapshot = wizardViewModel.wizardStage.collectAsState().value) {
        is WizardViewModel.WizardStage.Intro -> IntroSection(wizardStateSnapshot.introSectionViewModel) { wizardViewModel.next() }
        is WizardViewModel.WizardStage.Login ->
            WizardLoginSection(
                wizardStateSnapshot.loginViewModel,
                onForgotPassword
            ) { wizardViewModel.next() }
        is WizardViewModel.WizardStage.Assessment ->
            CreateAssessmentSection(
                wizardStateSnapshot.assessmentViewModel,
                isViewLastResult
            ) { wizardViewModel.next() }
        is WizardViewModel.WizardStage.Demographics ->
            DemographicsSection(
                wizardStateSnapshot.demographicsViewModel
            ) { wizardViewModel.next() }
        is WizardViewModel.WizardStage.Questions -> QuestionsSection(wizardStateSnapshot.questionsViewModel) { wizardViewModel.next() }
        is WizardViewModel.WizardStage.Insights -> InsightsSection(wizardStateSnapshot.resultsViewModel) { }
    }
}

/*
 * Gets a query string argument appropriate for the wizard to bypass creating an assessment and instead
 * show the last result.
 * Long-term, this will go away once we have UI to let users see their prior result. This is a temporary solution for
 * beta testing.
 */
private fun URLSearchParams.isShowLastResult(): Boolean = get("show_last_result").toBoolean()

private fun URLSearchParams.getEmail(): Email? {
    val emailBase64 = get(UserPath.ChangePassword.EMAIL)

    if (null != emailBase64) {
        val emailString = Base64.decode(emailBase64)
        if (EmailValidator.isValid(emailString)) {
            return Email.new(emailString)
        }
    }

    return null
}
